import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";

const RAW_DATA = [];

const DATE_FILTERS = [
  "Last 7 Days",
  "This Month",
  "Last 3 Months",
  "All Time",
  "Custom",
];

// 12 KPI Groups
const KPI_GROUPS = {
  "Lagging KPIs": {
    color: "#ff4757",
    label: "LAGGING KPIs",
    kpis: [
      "No. of Days Without LTI",
      "Fatality",
      "Lost Workday Case",
      "Restricted Workday Case",
      "Medical Treatment Case",
      "First Aid Case",
      "Process Fire",
      "Non-Process Fire",
      "Property Damage",
      "Road Accident",
      "Minor LOPC",
      "Environmental Incident",
      "Security Incident",
      "Occupational Illness",
    ],
  },
  "Leading KPIs": {
    color: "#00c9a7",
    label: "LEADING KPIs",
    kpis: [
      "Man Hours",
      "Near Miss",
      "Notice of Non-Compliance",
      "Contractor UA/UC",
      "Client Stop Work",
      "UA/UC",
    ],
  },
  "POB Fields": {
    color: "#00c9a7",
    label: "POB",
    hidden: true,
    kpis: ["POB", "POB Expat", "POB Local"],
  },
  Training: {
    color: "#4facfe",
    label: "TRAINING",
    kpis: [
      "Training Conducted",
      "Training No. of Participants",
      "Training Hours Spent",
      "Video Training Conducted",
      "Video No. of Participants",
      "Video Hours Spent",
      "Lesson Learned Training Conducted",
      "LL No. of Participants",
      "LL Hours Spent",
    ],
  },
  "HSE Induction & TBT": {
    color: "#a29bfe",
    label: "HSE INDUCTION & TBT",
    kpis: [
      "HSE Induction Conducted",
      "TBT Session Conducted",
      "Safety Stand Down (SSD)",
      "HSE Briefing Session",
    ],
  },
  PTW: {
    color: "#f9ca24",
    label: "PTW (PERMIT TO WORK)",
    kpis: ["Cold Work PTW Issued", "Hot Work PTW Issued", "PTW Audits"],
  },
  Inspection: {
    color: "#fd79a8",
    label: "INSPECTION",
    kpis: ["Inspection Conducted", "External Audits"],
  },
  "Leadership Audits": {
    color: "#e17055",
    label: "LEADERSHIP AUDITS",
    kpis: [
      "Leadership Field Audits / Walkdown",
      "Management Site Visit",
      "Management Stop Card",
      "Management TBT",
      "Management Walkdown",
    ],
  },
  "HSE Observation": {
    color: "#00b894",
    label: "HSE OBSERVATION",
    kpis: ["Hazard Hunt", "Observation UA/UC", "Anton Stop Card Issued"],
  },
  "Leadership & Recognition": {
    color: "#74b9ff",
    label: "LEADERSHIP & RECOGNITION",
    kpis: ["Senior Management Visits", "Employee Performance Rewards"],
  },
  Meetings: {
    color: "#55efc4",
    label: "MEETINGS",
    kpis: [
      "Weekly Meeting with Clients",
      "Daily Meeting",
      "Daily Online Meeting (Digtalk)",
      "Monthly Internal Meeting",
    ],
  },
  "HSE Engagement": {
    color: "#fdcb6e",
    label: "HSE ENGAGEMENT ACTIVITIES",
    kpis: [
      "HSE Alert Issued",
      "High Risk Activities Performed",
      "High Risk Activities Supervised",
      "HSE Drill Conducted",
      "HSE Campaign",
      "Special Campaigns",
      "Warning Letter Issued",
    ],
  },
  "Client Engagement": {
    color: "#6c5ce7",
    label: "CLIENT ENGAGEMENT & FEEDBACK",
    kpis: [
      "Client Engagement",
      "Client Appreciations",
      "Client Satisfaction Evaluations",
    ],
  },
};

const SIDEBAR_SECTIONS = [
  { title: "Date Range", isDate: true, items: DATE_FILTERS, color: "#4b5563" },
  ...Object.entries(KPI_GROUPS).map(([name, g]) => ({
    title: name,
    groupName: name,
    color: g.color,
    items: g.kpis,
  })),
];

function getCategory(kpi) {
  for (const [, g] of Object.entries(KPI_GROUPS)) {
    if (g.kpis.includes(kpi))
      return {
        color: g.color,
        label: g.label,
        bg: g.color + "18",
        border: g.color + "40",
      };
  }
  return {
    color: "#a29bfe",
    label: "GENERAL",
    bg: "rgba(162,155,254,0.15)",
    border: "rgba(162,155,254,0.3)",
  };
}

const BAR_COLORS = [
  "#ff4757",
  "#00c9a7",
  "#f9ca24",
  "#4facfe",
  "#a29bfe",
  "#fd79a8",
  "#e17055",
  "#74b9ff",
  "#00b894",
  "#fdcb6e",
  "#6c5ce7",
  "#55efc4",
];

function Tip({ active, payload, label, activeKey }) {
  if (!active || !payload?.length) return null;
  const items = activeKey
    ? payload.filter((p) => p.dataKey === activeKey)
    : payload;
  return (
    <div
      style={{
        background: "rgba(248,250,255,0.97)",
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 10,
        padding: "10px 14px",
        backdropFilter: "blur(16px)",
      }}
    >
      <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>
        {label}
      </div>
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: item.color || item.fill,
            }}
          />
          <span style={{ fontSize: 12, color: "#374151" }}>{item.name}:</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e2433" }}>
            {item.value?.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Local date helper — avoids UTC timezone shift
function localDate(d) {
  const dt = d ? new Date(d) : new Date();
  const yr = dt.getFullYear();
  const mo = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${day}`;
}

// ── API helpers ────────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem("hse_token");
}
function setToken(t) {
  localStorage.setItem("hse_token", t);
}
function clearToken() {
  localStorage.removeItem("hse_token");
}
function authHdr() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  };
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_URL}${path}`, { headers: authHdr(), ...opts });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

// ── Staff Management Component ──────────────────────────────────────────────
function StaffManagement({ apiFetch }) {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [msg, setMsg] = useState({ text: "", ok: true });

  const loadStaff = async () => {
    try {
      const data = await apiFetch("/api/auth/users");
      setStaffList(data.filter((u) => u.role !== "admin"));
    } catch (e) {
      setMsg({ text: e.message || "Failed to load staff", ok: false });
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const handleCreate = async () => {
    if (!newUser.username || !newUser.password)
      return setMsg({ text: "Username and password required.", ok: false });
    if (newUser.password.length < 6)
      return setMsg({
        text: "Password must be at least 6 characters.",
        ok: false,
      });
    setLoading(true);
    try {
      await apiFetch("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ ...newUser, role: "staff" }),
      });
      setNewUser({ username: "", password: "" });
      setMsg({ text: "Staff account created!", ok: true });
      loadStaff();
    } catch (e) {
      setMsg({ text: e.message || "Failed to create account.", ok: false });
    }
    setLoading(false);
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete staff account "${username}"?`)) return;
    try {
      await apiFetch(`/api/auth/users/${username}`, { method: "DELETE" });
      setMsg({ text: `"${username}" deleted.`, ok: true });
      loadStaff();
    } catch (e) {
      setMsg({ text: e.message || "Failed to delete.", ok: false });
    }
  };

  const inputStyle = {
    width: "100%",
    background: "rgba(0,0,0,0.04)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    color: "#1e2433",
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 12,
        }}
      >
        Create Staff Account
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <input
          placeholder="Username"
          value={newUser.username}
          onChange={(e) =>
            setNewUser((p) => ({ ...p, username: e.target.value }))
          }
          style={inputStyle}
        />
        <input
          type="password"
          placeholder="Password (min 6 chars)"
          value={newUser.password}
          onChange={(e) =>
            setNewUser((p) => ({ ...p, password: e.target.value }))
          }
          style={inputStyle}
        />
        <button
          onClick={handleCreate}
          disabled={loading}
          style={{
            padding: "8px",
            borderRadius: 8,
            background: "linear-gradient(135deg,#00a8ff,#0078d4)",
            border: "none",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {loading ? "Creating..." : "Create Account"}
        </button>
      </div>

      {msg.text && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 7,
            fontSize: 12,
            marginBottom: 10,
            background: msg.ok
              ? "rgba(0,201,167,0.12)"
              : "rgba(255,71,87,0.12)",
            border: `1px solid ${msg.ok ? "rgba(0,201,167,0.3)" : "rgba(255,71,87,0.3)"}`,
            color: msg.ok ? "#00b894" : "#ff4757",
          }}
        >
          {msg.text}
        </div>
      )}

      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 8,
        }}
      >
        Staff Accounts ({staffList.length})
      </div>
      {staffList.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "#94a3b8",
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          No staff accounts yet
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {staffList.map((u) => (
            <div
              key={u.username}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "rgba(0,0,0,0.03)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#1e2433" }}
                >
                  {u.username}
                </div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>Staff</div>
              </div>
              <button
                onClick={() => handleDelete(u.username)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: "rgba(255,71,87,0.1)",
                  border: "1px solid rgba(255,71,87,0.25)",
                  color: "#ff4757",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Login Screen ───────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please enter username and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f4ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Poppins,sans-serif",
      }}
    >
      <style>{`
        .login-input:focus {
          border-color: #0078d4 !important;
          box-shadow: 0 0 0 3px rgba(0,120,212,0.12) !important;
        }
        .login-btn:hover:not(:disabled) { opacity: 0.88; }
      `}</style>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: "44px 48px",
          width: 400,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        }}
      >
        {/* Brand Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: "linear-gradient(135deg,#0078d4,#005a9e)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
              boxShadow: "0 4px 14px rgba(0,120,212,0.3)",
            }}
          >
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
              HSE
            </span>
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            HSE Desk
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Health, Safety & Environment Portal
          </div>
        </div>

        <div style={{ height: 1, background: "#e2e8f0", marginBottom: 28 }} />

        {error && (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 20,
              color: "#e11d48",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#374151",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              Username
            </label>
            <input
              className="login-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              style={{
                width: "100%",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#0f172a",
                fontSize: 14,
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxSizing: "border-box",
                fontFamily: "Poppins,sans-serif",
              }}
              required
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#374151",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              Password
            </label>
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{
                width: "100%",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#0f172a",
                fontSize: 14,
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxSizing: "border-box",
                fontFamily: "Poppins,sans-serif",
              }}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="login-btn"
            style={{
              width: "100%",
              background: loading
                ? "#93c5fd"
                : "linear-gradient(135deg,#0078d4,#005a9e)",
              border: "none",
              borderRadius: 8,
              padding: "12px",
              color: "#ffffff",
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: 0.5,
              transition: "opacity 0.2s",
              fontFamily: "Poppins,sans-serif",
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div
          style={{
            textAlign: "center",
            marginTop: 20,
            fontSize: 12,
            color: "#94a3b8",
          }}
        >
          Contact your administrator for access
        </div>
      </div>
    </div>
  );
}

export default function HSEDashboard() {
  // ── Auth state ──────────────────────────────────────────────────────────
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [filter, setFilter] = useState("All Time");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [focusGroup, setFocusGroup] = useState(null);
  const [expandedSections, setExpanded] = useState({ "Date Range": true });
  const [activeBar, setActiveBar] = useState(null);
  const [pobFilter, setPobFilter] = useState("Daily");

  const [uploadData, setUploadData] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showAllTable, setShowAllTable] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 12;
  const [showAddData, setShowAddData] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [addDate, setAddDate] = useState(localDate());
  const [addLabel, setAddLabel] = useState("");
  const [addValues, setAddValues] = useState({ "No. of Days Without LTI": 1 });
  const [addSaved, setAddSaved] = useState(false);
  const [editRow, setEditRow] = useState(null); // row being edited
  const [editValues, setEditValues] = useState({});
  const [editStep, setEditStep] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // date string of row to delete
  const fileInputRef = useRef(null);

  const activeData = uploadData;

  async function loadEntriesFromAPI() {
    try {
      const data = await apiFetch("/api/entries");
      setUploadData(data.entries || []);
      setApiError("");
    } catch (err) {
      console.error("Load error:", err);
      setApiError("Cannot connect to backend. Make sure server is running.");
    }
  }

  function handleLogin(user) {
    setCurrentUser(user);
    loadEntriesFromAPI();
  }

  function handleLogout() {
    clearToken();
    setCurrentUser(null);
    setUploadData([]);
  }

  // Load SheetJS on mount
  useEffect(() => {
    if (window.XLSX) return;
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    document.head.appendChild(s);
  }, []);

  const focusKPI = selectedKPIs[0] || null;
  const focusCat = focusKPI
    ? getCategory(focusKPI)
    : {
        color: "#a29bfe",
        bg: "rgba(162,155,254,0.15)",
        border: "rgba(162,155,254,0.3)",
        label: "GENERAL",
      };

  const filteredData = useMemo(() => {
    const now = new Date();
    const todayStr = localDate(now);

    if (filter === "All Time") return activeData;

    if (filter === "Custom") {
      if (!customFrom && !customTo) return activeData;
      return activeData.filter((r) => {
        if (customFrom && customTo)
          return r.date >= customFrom && r.date <= customTo;
        if (customFrom) return r.date >= customFrom;
        if (customTo) return r.date <= customTo;
        return true;
      });
    }

    const cutoff = new Date(now);
    if (filter === "Last 7 Days") cutoff.setDate(now.getDate() - 7);
    if (filter === "This Month") cutoff.setDate(1);
    if (filter === "Last 3 Months") cutoff.setMonth(now.getMonth() - 3);

    const cutoffStr = localDate(cutoff);
    return activeData.filter((r) => r.date >= cutoffStr && r.date <= todayStr);
  }, [filter, customFrom, customTo, activeData]);

  // ── SMART CHART GROUPING ──────────────────────────────────────────────────
  // If filtered period spans <= 31 days → show each row as-is (daily)
  // If filtered period spans > 31 days  → aggregate rows by month
  const chartData = useMemo(() => {
    if (!filteredData.length) return [];

    const allKPIs = Object.values(KPI_GROUPS).flatMap((g) => g.kpis);

    // Check date span
    const dates = filteredData.map((r) => r.date).sort();
    const spanDays =
      (new Date(dates[dates.length - 1]) - new Date(dates[0])) / 86400000;

    if (spanDays <= 31) {
      // Daily — use rows as-is
      return filteredData;
    }

    // Monthly — aggregate by YYYY-MM
    const buckets = {};
    filteredData.forEach((r) => {
      const key = r.date.slice(0, 7); // "2026-03"
      if (!buckets[key]) {
        const dp = r.date.split("-");
        const d = new Date(
          parseInt(dp[0]),
          parseInt(dp[1]) - 1,
          parseInt(dp[2]),
        );
        buckets[key] = {
          date: key + "-01",
          label: d.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          }),
        };
        allKPIs.forEach((k) => {
          buckets[key][k] = 0;
        });
      }
      allKPIs.forEach((k) => {
        buckets[key][k] += r[k] || 0;
      });
    });

    return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData]);

  // ─────────────────────────────────────────────────────────────────────────

  // T = filtered totals (used for summary cards, charts, group view — always respects active filter)
  const T = useMemo(() => {
    const totals = {};
    const source = filteredData.length > 0 ? filteredData : activeData;
    source.forEach((r) =>
      Object.keys(r).forEach((k) => {
        if (k !== "date" && k !== "label")
          totals[k] = (totals[k] || 0) + (r[k] || 0);
      }),
    );
    return totals;
  }, [filteredData, activeData]);

  // T_all = all-time totals (used only in Multi-KPI detail table)
  const T_all = useMemo(() => {
    const totals = {};
    activeData.forEach((r) =>
      Object.keys(r).forEach((k) => {
        if (k !== "date" && k !== "label")
          totals[k] = (totals[k] || 0) + (r[k] || 0);
      }),
    );
    return totals;
  }, [activeData]);

  // POB card: Daily = latest entry, All Time = T_all totals
  const pobData = useMemo(() => {
    if (pobFilter === "Daily") {
      const latest = activeData[activeData.length - 1];
      if (!latest) return { expat: 0, local: 0, pob: 0 };
      return {
        expat: latest["POB Expat"] || 0,
        local: latest["POB Local"] || 0,
        pob: latest["POB"] || 0,
      };
    }
    return {
      expat: T_all["POB Expat"] || 0,
      local: T_all["POB Local"] || 0,
      pob: T_all["POB"] || 0,
    };
  }, [pobFilter, activeData, T_all]);

  function handleCSV() {
    if (!selectedKPIs.length) return;
    const rows = [
      ["Date", ...selectedKPIs],
      ...filteredData.map((r) => [r.date, ...selectedKPIs.map((k) => r[k])]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = `selected_kpis.csv`;
    a.click();
  }

  function toggleSection(title) {
    setExpanded((prev) => ({ ...prev, [title]: !prev[title] }));
  }

  const focusGroupData = focusGroup ? KPI_GROUPS[focusGroup] : null;

  // Excel parse function
  const parseExcel = useCallback((file) => {
    setUploadError("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Use SheetJS from global scope (loaded via script in index)
        const XLSX = window.XLSX;
        if (!XLSX) {
          setUploadError("Excel parser not loaded yet. Please try again.");
          return;
        }
        const wb = XLSX.read(e.target.result, {
          type: "array",
          cellDates: true,
        });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: 0 });
        if (!rows.length) {
          setUploadError("Sheet is empty.");
          return;
        }

        // Normalize: find date column, map to our format
        const parsed = rows.map((row, i) => {
          // Try common date column names
          const dateVal =
            row["Date"] ||
            row["date"] ||
            row["Month"] ||
            row["month"] ||
            row["DATE"] ||
            null;
          let dateStr, label;
          if (dateVal instanceof Date) {
            dateStr = localDate(dateVal);
            label = dateVal.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          } else if (typeof dateVal === "string" && dateVal.trim()) {
            const d = new Date(dateVal);
            dateStr = isNaN(d)
              ? "2026-" + String(i + 1).padStart(2, "0") + "-01"
              : localDate(d);
            label = isNaN(d)
              ? dateVal
              : d.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                });
          } else {
            dateStr = "2026-" + String(i + 1).padStart(2, "0") + "-01";
            label = "Row " + (i + 1);
          }
          const obj = { date: dateStr, label };
          Object.keys(row).forEach((k) => {
            if (!["Date", "date", "Month", "month", "DATE"].includes(k)) {
              obj[k] = isNaN(Number(row[k])) ? 0 : Number(row[k]);
            }
          });
          return obj;
        });
        // Save to API if connected, else local only
        try {
          await apiFetch("/api/entries/bulk", {
            method: "POST",
            body: JSON.stringify({ entries: parsed }),
          });
          await loadEntriesFromAPI();
        } catch {
          // API not available - use local state
          setUploadData(parsed);
        }
        setShowUpload(false);
        setFilter("All Time");
      } catch (err) {
        setUploadError("Error reading file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  function exportCSV() {
    const allKPIs = Object.values(KPI_GROUPS).flatMap((g) => g.kpis);
    const rows = [["Month", ...allKPIs]];
    filteredData.forEach((r) =>
      rows.push([r.label, ...allKPIs.map((k) => r[k] ?? 0)]),
    );
    // totals row
    rows.push([
      "TOTAL",
      ...allKPIs.map((k) => filteredData.reduce((s, r) => s + (r[k] || 0), 0)),
    ]);
    const csv = rows
      .map((r) =>
        r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(","),
      )
      .join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "HSE_Data_" + filter.replace(/ /g, "_") + ".csv";
    a.click();
  }

  function exportExcel() {
    const XLSX = window.XLSX;
    if (!XLSX) {
      alert("Excel library not loaded yet, please try CSV export.");
      return;
    }
    const allKPIs = Object.values(KPI_GROUPS).flatMap((g) => g.kpis);
    const wsData = [["Month", ...allKPIs]];
    filteredData.forEach((r) =>
      wsData.push([r.label, ...allKPIs.map((k) => r[k] ?? 0)]),
    );
    wsData.push([
      "TOTAL",
      ...allKPIs.map((k) => filteredData.reduce((s, r) => s + (r[k] || 0), 0)),
    ]);
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // column widths
    ws["!cols"] = [{ wch: 14 }, ...allKPIs.map(() => ({ wch: 18 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSE Data");
    XLSX.writeFile(wb, "HSE_Data_" + filter.replace(/ /g, "_") + ".xlsx");
  }

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) parseExcel(file);
    },
    [parseExcel],
  );

  // Font for whole app
  const font = "'Poppins','Segoe UI',sans-serif";

  // ── Auth state ──
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // ── Profile dropdown state ──
  const [showProfile, setShowProfile] = useState(false);
  const [profileTab, setProfileTab] = useState("info");
  const [newUsername, setNewUsername] = useState("");
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [profileMsg, setProfileMsg] = useState({ text: "", ok: true });

  // ── Check token on mount ──
  useEffect(() => {
    async function checkAuth() {
      const token = getToken();
      if (!token) {
        setAuthChecked(true);
        return;
      }
      try {
        const data = await apiFetch("/api/auth/me");
        setCurrentUser(data.user);
        await loadEntriesFromAPI();
      } catch {
        clearToken();
      } finally {
        setAuthChecked(true);
      }
    }
    checkAuth();
  }, []);

  // ── Auth guard ──
  if (!authChecked)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f0f4ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Poppins,sans-serif",
          color: "rgba(0,0,0,0.40)",
          fontSize: 14,
        }}
      >
        Loading...
      </div>
    );
  if (!currentUser) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background:
          "linear-gradient(135deg,#f0f4ff 0%,#eef2ff 60%,#f5f0ff 100%)",
        fontFamily: font,
        color: "#1e2433",
        overflow: "hidden",
      }}
    >
      {/* ── SIDEBAR ── */}
      <div
        style={{
          width: 220,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          background: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(20px)",
          borderRight: "1px solid rgba(0,0,0,0.09)",
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: "18px 16px 14px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "linear-gradient(135deg,#00a8ff,#0078d4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              fontWeight: 800,
              color: "#1e2433",
              flexShrink: 0,
            }}
          >
            H
          </div>
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#1e2433",
                letterSpacing: 0.5,
              }}
            >
              HSE Desk
            </div>
            <div style={{ fontSize: 9, color: "#6b7280" }}>
              Safety Dashboard
            </div>
          </div>
        </div>
        {/* Sections */}
        <div style={{ flex: 1, padding: "8px 0" }}>
          {SIDEBAR_SECTIONS.map((sec, si) => {
            const isExpanded = !!expandedSections[sec.title];
            const isGroupActive = sec.groupName && focusGroup === sec.groupName;
            return (
              <div key={si}>
                <div
                  onClick={() => {
                    toggleSection(sec.title);
                    if (!sec.isDate && sec.groupName) {
                      setFocusGroup(
                        focusGroup === sec.groupName ? null : sec.groupName,
                      );
                      setSelectedKPIs([]);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    cursor: "pointer",
                    background: isGroupActive
                      ? `${sec.color}18`
                      : "transparent",
                    borderLeft: `3px solid ${isGroupActive ? sec.color : "transparent"}`,
                    transition: "all 0.15s",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: isGroupActive ? sec.color : "#374151",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {sec.title}
                  </span>
                  <span style={{ fontSize: 10, color: "#6b7280" }}>
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </div>
                {isExpanded && (
                  <div style={{ paddingLeft: 8 }}>
                    {sec.items.map((item, ii) => {
                      const isDateFilter = sec.isDate;
                      const isActive = isDateFilter
                        ? filter === item
                        : selectedKPIs.includes(item);
                      return (
                        <div
                          key={ii}
                          className={`sidebar-item${isActive ? " active" : ""}`}
                          style={{
                            borderLeft: `2px solid ${isActive ? sec.color || "#00c9a7" : "rgba(0,0,0,0.04)"}`,
                            borderRadius: "0 8px 8px 0",
                            marginBottom: 2,
                            transition: "all 0.15s",
                          }}
                          onClick={() => {
                            if (isDateFilter) {
                              setFilter(item);
                            } else {
                              setSelectedKPIs((prev) =>
                                prev.includes(item)
                                  ? prev.filter((k) => k !== item)
                                  : [...prev, item],
                              );
                              setFocusGroup(null);
                            }
                          }}
                        >
                          <span
                            style={{
                              color: isActive
                                ? sec.color || "#00c9a7"
                                : "#4b5563",
                              fontSize: 11,
                              fontWeight: isActive ? 600 : 400,
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      );
                    })}
                    {/* Custom date inputs */}
                    {sec.isDate && filter === "Custom" && (
                      <div style={{ padding: "8px 10px 10px" }}>
                        <div
                          style={{
                            fontSize: 9,
                            color: "#6b7280",
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          From
                        </div>
                        <input
                          type="date"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                          style={{
                            width: "100%",
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.10)",
                            borderRadius: 6,
                            padding: "5px 8px",
                            color: "#1e2433",
                            fontSize: 11,
                            fontFamily: font,
                            outline: "none",
                            marginBottom: 6,
                          }}
                        />
                        <div
                          style={{
                            fontSize: 9,
                            color: "#6b7280",
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          To
                        </div>
                        <input
                          type="date"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                          style={{
                            width: "100%",
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.10)",
                            borderRadius: 6,
                            padding: "5px 8px",
                            color: "#1e2433",
                            fontSize: 11,
                            fontFamily: font,
                            outline: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── MAIN ── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 24px",
            background: "rgba(255,255,255,0.93)",
            backdropFilter: "blur(20px)",
            borderBottom: "1px solid rgba(0,0,0,0.09)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1e2433" }}>
              HSE DESK
            </div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>
              Safety Analytics Dashboard
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {(selectedKPIs.length > 0 || focusGroup) && (
              <button
                onClick={() => {
                  setSelectedKPIs([]);
                  setFocusGroup(null);
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.10)",
                  color: "#4b5563",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: font,
                }}
              >
                All KPIs
              </button>
            )}
            <button
              onClick={() => {
                setShowAddData(true);
                setAddStep(0);
                setAddValues({ "No. of Days Without LTI": 1 });
                setAddSaved(false);
                setAddDate(localDate());
                setAddLabel("");
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                background: "rgba(249,202,36,0.15)",
                border: "1px solid rgba(249,202,36,0.35)",
                color: "#f9ca24",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              + Add Data
            </button>
            <button
              onClick={() => setShowExport(true)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                background: "rgba(0,201,167,0.15)",
                border: "1px solid rgba(0,201,167,0.3)",
                color: "#00c9a7",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Export
            </button>
            <button
              onClick={() => setShowUpload(true)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                background: "rgba(79,172,254,0.15)",
                border: "1px solid rgba(79,172,254,0.3)",
                color: "#4facfe",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Upload Excel
            </button>
            <div
              style={{
                background: "rgba(0,201,167,0.12)",
                border: "1px solid rgba(0,201,167,0.25)",
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 11,
                color: "#00c9a7",
                fontWeight: 600,
              }}
            >
              Live
            </div>
            <div style={{ fontSize: 11, color: "#4b5563" }}>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            {/* Profile Icon */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => {
                  setShowProfile((p) => !p);
                  setProfileTab("info");
                  setProfileMsg({ text: "", ok: true });
                }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#00a8ff,#0078d4)",
                  border: "2px solid rgba(0,168,255,0.4)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 12px rgba(0,168,255,0.3)",
                  flexShrink: 0,
                }}
                title={currentUser?.username}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#1e2433",
                    fontFamily: font,
                  }}
                >
                  {(currentUser?.username || "U").charAt(0).toUpperCase()}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* ── GROUP VIEW ── */}
          {focusGroup && !selectedKPIs.length ? (
            <div>
              {/* Banner */}
              <div
                style={{
                  background: `linear-gradient(135deg,${focusGroupData.color}18,${focusGroupData.color}08)`,
                  border: `1px solid ${focusGroupData.color}30`,
                  borderRadius: 16,
                  padding: "20px 26px",
                  marginBottom: 18,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 14,
                  backdropFilter: "blur(16px)",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: focusGroupData.color,
                      fontWeight: 500,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Category View
                  </div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: "#1e2433" }}
                  >
                    {focusGroup}
                  </div>
                  <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                    {focusGroupData.kpis.length} indicators · click any card to
                    drill down
                  </div>
                </div>
                <button
                  onClick={() => {
                    const rows = [
                      ["Date", ...focusGroupData.kpis],
                      ...filteredData.map((r) => [
                        r.date,
                        ...focusGroupData.kpis.map((k) => r[k]),
                      ]),
                    ];
                    const a = document.createElement("a");
                    a.href =
                      "data:text/csv," +
                      encodeURIComponent(
                        rows.map((r) => r.join(",")).join("\n"),
                      );
                    a.download = `${focusGroup}.csv`;
                    a.click();
                  }}
                  style={{
                    padding: "9px 20px",
                    borderRadius: 10,
                    background: focusGroupData.color,
                    border: "none",
                    color: "#f0f4ff",
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: font,
                  }}
                >
                  ↓ CSV
                </button>
              </div>
              {/* KPI Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                {focusGroupData.kpis.map((kpi) => (
                  <div
                    key={kpi}
                    className="kpi-card"
                    onClick={() => {
                      setSelectedKPIs([kpi]);
                      setFocusGroup(null);
                    }}
                    style={{
                      background: "rgba(255,255,255,0.88)",
                      border: `1px solid rgba(0,0,0,0.05)`,
                      borderRadius: 12,
                      padding: "14px 16px",
                      cursor: "pointer",
                      backdropFilter: "blur(12px)",
                      transition: "all 0.2s",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: focusGroupData.color,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        marginBottom: 6,
                        lineHeight: 1.3,
                      }}
                    >
                      {kpi}
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#1e2433",
                      }}
                    >
                      {T[kpi]?.toLocaleString()}
                    </div>
                    <div
                      style={{ fontSize: 10, color: "#6b7280", marginTop: 4 }}
                    >
                      {filter === "All Time"
                        ? "All time total"
                        : filter === "Custom"
                          ? `${customFrom} – ${customTo}`
                          : `${filter} total`}
                    </div>
                  </div>
                ))}
              </div>
              {/* Group Chart */}
              <div className="card">
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {focusGroupData.label} —{" "}
                    {filter === "Custom"
                      ? `${customFrom} – ${customTo}`
                      : filter}
                  </span>
                  {filteredData.length > 0 && (
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>
                      {filter === "Custom"
                        ? `${customFrom} – ${customTo}`
                        : filter}
                    </span>
                  )}
                </div>
                {filteredData.length === 0 ? (
                  <div
                    style={{
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    No data for selected period
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        justifyContent: "center",
                        padding: "8px 0",
                      }}
                    >
                      {focusGroupData.kpis.map((kpi, i) => {
                        const sel = T[kpi] || 0;
                        const all = T_all[kpi] || 0;
                        const circ = 226.19;
                        const arc =
                          all > 0
                            ? Math.min((sel / all) * circ, circ).toFixed(1)
                            : 0;
                        const kpiColorOverrides = {
                          "Cold Work PTW Issued": "#22c55e",
                          "Hot Work PTW Issued": "#ef4444",
                          "PTW Audits": "#38bdf8",
                        };
                        const color =
                          kpiColorOverrides[kpi] ||
                          [
                            "#2563eb",
                            "#059669",
                            "#7c3aed",
                            "#d97706",
                            "#dc2626",
                            "#0284c7",
                            "#9333ea",
                            "#16a34a",
                            "#ea580c",
                            "#0891b2",
                            "#be185d",
                            "#15803d",
                          ][i % 12];
                        return (
                          <div
                            key={kpi}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              minWidth: 100,
                            }}
                          >
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg width="96" height="96" viewBox="0 0 96 96">
                                <circle
                                  cx="48"
                                  cy="48"
                                  r="36"
                                  fill="none"
                                  stroke="#f1f5f9"
                                  strokeWidth="10"
                                />
                                <circle
                                  cx="48"
                                  cy="48"
                                  r="36"
                                  fill="none"
                                  stroke={color}
                                  strokeWidth="10"
                                  strokeDasharray={`${arc} ${circ}`}
                                  strokeLinecap="round"
                                  transform="rotate(-90 48 48)"
                                />
                              </svg>
                              <div
                                style={{
                                  position: "absolute",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 18,
                                    fontWeight: 900,
                                    color: color,
                                    lineHeight: 1,
                                  }}
                                >
                                  {sel}
                                </div>
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontWeight: 600,
                                    color: "#94a3b8",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                    marginTop: 1,
                                  }}
                                >
                                  sel
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: color,
                                marginTop: 6,
                                textAlign: "center",
                                maxWidth: 90,
                                lineHeight: 1.2,
                              }}
                            >
                              {kpi}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#64748b",
                                marginTop: 2,
                              }}
                            >
                              All:{" "}
                              <span
                                style={{ fontWeight: 700, color: "#374151" }}
                              >
                                {all}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: 11,
                        color: "#94a3b8",
                        marginTop: 14,
                        paddingTop: 10,
                        borderTop: "1px solid #f1f5f9",
                        fontWeight: 500,
                      }}
                    >
                      {filteredData.length > 0
                        ? filteredData.length === 1
                          ? filteredData[0].label
                          : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                        : filter}
                    </div>
                  </div>
                )}
              </div>
              {/* Group Table */}
              <div
                className="card"
                style={{ marginTop: 16, overflowX: "auto" }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Data Table</span>
                  {filteredData.length > 6 && (
                    <button
                      onClick={() => setShowAllTable((v) => !v)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: "rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        color: "#4b5563",
                        fontSize: 10,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      {showAllTable
                        ? "Show less"
                        : "Show all " + filteredData.length + " months"}
                    </button>
                  )}
                </div>
                {filteredData.length === 0 ? (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    No data for selected period
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "1px solid rgba(0,0,0,0.05)",
                        }}
                      >
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            color: "#6b7280",
                            fontSize: 10,
                            textTransform: "uppercase",
                          }}
                        >
                          KPI
                        </th>
                        {(showAllTable
                          ? filteredData
                          : filteredData.slice(-6)
                        ).map((r, i) => (
                          <th
                            key={i}
                            style={{
                              textAlign: "right",
                              padding: "8px 12px",
                              color: "#4b5563",
                              fontSize: 10,
                            }}
                          >
                            {r.label}
                          </th>
                        ))}
                        <th
                          style={{
                            textAlign: "right",
                            padding: "8px 12px",
                            color: focusGroupData.color,
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        >
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {focusGroupData.kpis.map((kpi) => (
                        <tr
                          key={kpi}
                          style={{
                            borderBottom: "1px solid rgba(255,255,255,0.88)",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(255,255,255,0.75)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <td
                            style={{
                              padding: "10px 12px",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            {kpi}
                          </td>
                          {(showAllTable
                            ? filteredData
                            : filteredData.slice(-6)
                          ).map((r, i) => (
                            <td
                              key={i}
                              style={{
                                padding: "10px 12px",
                                textAlign: "right",
                                fontSize: 15,
                                fontWeight: 700,
                                color: focusGroupData.color,
                              }}
                            >
                              {r[kpi] || 0}
                            </td>
                          ))}
                          <td
                            style={{
                              padding: "10px 12px",
                              textAlign: "right",
                              fontSize: 16,
                              fontWeight: 800,
                              color: "#1e2433",
                            }}
                          >
                            {T[kpi]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {filteredData.length > 6 && !showAllTable && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px 0 4px",
                      fontSize: 10,
                      color: "#9ca3af",
                    }}
                  >
                    Showing last 6 of {filteredData.length} months
                  </div>
                )}
              </div>
            </div>
          ) : selectedKPIs.length > 0 ? (
            /* ── MULTI-KPI VIEW ── */
            <div>
              {/* Banner */}
              <div
                style={{
                  background: `linear-gradient(135deg,${focusCat.color}18,${focusCat.color}08)`,
                  border: `1px solid ${focusCat.color}30`,
                  borderRadius: 16,
                  padding: "20px 26px",
                  marginBottom: 18,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 14,
                  backdropFilter: "blur(16px)",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: focusCat.color,
                      fontWeight: 500,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    {selectedKPIs.length === 1
                      ? "Focused View"
                      : `Comparing ${selectedKPIs.length} KPIs`}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#1e2433",
                      lineHeight: 1.4,
                    }}
                  >
                    {selectedKPIs.join(" · ")}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#4b5563",
                      marginTop: 6,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    {selectedKPIs.map((k) => (
                      <span key={k}>
                        <span style={{ color: getCategory(k).color }}>
                          {k}:
                        </span>{" "}
                        <strong style={{ color: "#1e2433" }}>
                          {T[k]?.toLocaleString() ?? 0}
                        </strong>
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={handleCSV}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      background: focusCat.color,
                      border: "none",
                      color: "#f0f4ff",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: font,
                    }}
                  >
                    ↓ CSV
                  </button>
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      background: "transparent",
                      border: `1px solid ${focusCat.border}`,
                      color: focusCat.color,
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: font,
                    }}
                  >
                    Print
                  </button>
                </div>
              </div>
              {/* Chart */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{ fontSize: 13, fontWeight: 500, color: "#4b5563" }}
                  >
                    Monthly Comparison
                  </div>
                  <div style={{ fontSize: 10, color: "#6b7280" }}>
                    {filter === "Custom"
                      ? `${customFrom} – ${customTo}`
                      : filter}
                  </div>
                </div>
                {filteredData.length === 0 ? (
                  <div
                    style={{
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    No data for selected period
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        justifyContent: "center",
                        padding: "8px 0",
                      }}
                    >
                      {selectedKPIs.map((kpi, i) => {
                        const sel = T[kpi] || 0;
                        const all = T_all[kpi] || 0;
                        const circ = 226.19;
                        const arc =
                          all > 0
                            ? Math.min((sel / all) * circ, circ).toFixed(1)
                            : 0;
                        const kpiColorOverrides = {
                          "Cold Work PTW Issued": "#22c55e",
                          "Hot Work PTW Issued": "#ef4444",
                          "PTW Audits": "#38bdf8",
                        };
                        const color =
                          kpiColorOverrides[kpi] ||
                          [
                            "#2563eb",
                            "#059669",
                            "#7c3aed",
                            "#d97706",
                            "#dc2626",
                            "#0284c7",
                            "#9333ea",
                            "#16a34a",
                            "#ea580c",
                            "#0891b2",
                            "#be185d",
                            "#15803d",
                          ][i % 12];
                        return (
                          <div
                            key={kpi}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              minWidth: 100,
                            }}
                          >
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg width="96" height="96" viewBox="0 0 96 96">
                                <circle
                                  cx="48"
                                  cy="48"
                                  r="36"
                                  fill="none"
                                  stroke="#f1f5f9"
                                  strokeWidth="10"
                                />
                                <circle
                                  cx="48"
                                  cy="48"
                                  r="36"
                                  fill="none"
                                  stroke={color}
                                  strokeWidth="10"
                                  strokeDasharray={`${arc} ${circ}`}
                                  strokeLinecap="round"
                                  transform="rotate(-90 48 48)"
                                />
                              </svg>
                              <div
                                style={{
                                  position: "absolute",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 18,
                                    fontWeight: 900,
                                    color: color,
                                    lineHeight: 1,
                                  }}
                                >
                                  {sel}
                                </div>
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontWeight: 600,
                                    color: "#94a3b8",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                    marginTop: 1,
                                  }}
                                >
                                  sel
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: color,
                                marginTop: 6,
                                textAlign: "center",
                                maxWidth: 90,
                                lineHeight: 1.2,
                              }}
                            >
                              {kpi}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#64748b",
                                marginTop: 2,
                              }}
                            >
                              All:{" "}
                              <span
                                style={{ fontWeight: 700, color: "#374151" }}
                              >
                                {all}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: 11,
                        color: "#94a3b8",
                        marginTop: 14,
                        paddingTop: 10,
                        borderTop: "1px solid #f1f5f9",
                        fontWeight: 500,
                      }}
                    >
                      {filteredData.length > 0
                        ? filteredData.length === 1
                          ? filteredData[0].label
                          : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                        : filter}
                    </div>
                  </div>
                )}
              </div>
              {/* All-time table */}
              <div className="card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#4b5563",
                      }}
                    >
                      Detail Table
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        background: "rgba(0,201,167,0.12)",
                        border: "1px solid rgba(0,201,167,0.2)",
                        color: "#00c9a7",
                        borderRadius: 6,
                        padding: "2px 8px",
                      }}
                    >
                      All Months
                    </div>
                  </div>
                  {activeData.length > 6 && (
                    <button
                      onClick={() => setShowAllTable((v) => !v)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: "rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        color: "#4b5563",
                        fontSize: 10,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      {showAllTable
                        ? "Show less"
                        : "Show all " + activeData.length + " months"}
                    </button>
                  )}
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "1px solid rgba(0,0,0,0.05)",
                        }}
                      >
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            color: "#6b7280",
                            fontWeight: 600,
                            fontSize: 10,
                            textTransform: "uppercase",
                          }}
                        >
                          Date
                        </th>
                        {selectedKPIs.map((k) => (
                          <th
                            key={k}
                            style={{
                              textAlign: "right",
                              padding: "8px 12px",
                              color: getCategory(k).color,
                              fontWeight: 600,
                              fontSize: 10,
                            }}
                          >
                            {k}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllTable ? activeData : activeData.slice(-6)).map(
                        (row, i) => (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.88)",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background =
                                "rgba(255,255,255,0.75)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <td
                              style={{
                                padding: "10px 12px",
                                color: "#4b5563",
                                fontWeight: 500,
                              }}
                            >
                              {row.label}
                            </td>
                            {selectedKPIs.map((k) => (
                              <td
                                key={k}
                                style={{
                                  padding: "10px 12px",
                                  textAlign: "right",
                                  fontSize: 16,
                                  fontWeight: 700,
                                  color: getCategory(k).color,
                                }}
                              >
                                {row[k] || 0}
                              </td>
                            ))}
                          </tr>
                        ),
                      )}
                      <tr
                        style={{
                          borderTop: "2px solid rgba(0,0,0,0.06)",
                          background: "rgba(255,255,255,0.75)",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 12px",
                            color: "#374151",
                            fontWeight: 700,
                          }}
                        >
                          TOTAL
                        </td>
                        {selectedKPIs.map((k) => (
                          <td
                            key={k}
                            style={{
                              padding: "10px 12px",
                              textAlign: "right",
                              fontSize: 18,
                              fontWeight: 800,
                              color: getCategory(k).color,
                            }}
                          >
                            {T_all[k]?.toLocaleString() || 0}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  {activeData.length > 6 && !showAllTable && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "10px 0 4px",
                        fontSize: 10,
                        color: "#9ca3af",
                      }}
                    >
                      Showing last 6 of {activeData.length} months
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* ── OVERVIEW ── */
            <div>
              {/* No data at all — prompt to add */}
              {activeData.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 24px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 16,
                      background: "rgba(249,202,36,0.1)",
                      border: "1px solid rgba(249,202,36,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 20,
                    }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#f9ca24"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#1e2433",
                      marginBottom: 8,
                    }}
                  >
                    No Data Yet
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#9ca3af",
                      marginBottom: 28,
                      maxWidth: 340,
                      lineHeight: 1.7,
                    }}
                  >
                    Abhi koi record nahi hai. "Add Data" se daily entry karein
                    ya Excel file upload karein.
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      onClick={() => {
                        setShowAddData(true);
                        setAddStep(0);
                        setAddValues({ "No. of Days Without LTI": 1 });
                        setAddSaved(false);
                        setAddDate(localDate());
                        setAddLabel("");
                      }}
                      style={{
                        padding: "10px 24px",
                        borderRadius: 10,
                        background: "rgba(249,202,36,0.15)",
                        border: "1px solid rgba(249,202,36,0.35)",
                        color: "#f9ca24",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      + Add Data
                    </button>
                    <button
                      onClick={() => setShowUpload(true)}
                      style={{
                        padding: "10px 24px",
                        borderRadius: 10,
                        background: "rgba(79,172,254,0.1)",
                        border: "1px solid rgba(79,172,254,0.25)",
                        color: "#4facfe",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Upload Excel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Filter info bar */}
                  {filteredData.length === 0 ? (
                    <div
                      style={{
                        background: "rgba(255,71,87,0.08)",
                        border: "1px solid rgba(255,71,87,0.25)",
                        borderRadius: 14,
                        padding: "32px 24px",
                        marginBottom: 20,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#ff6b81",
                          marginBottom: 6,
                        }}
                      >
                        No data for this period
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          marginBottom: 16,
                        }}
                      >
                        Filter{" "}
                        <strong style={{ color: "#1e2433" }}>{filter}</strong>{" "}
                        mein koi record nahi mila.
                        {customFrom || customTo
                          ? ` (${customFrom || "..."} to ${customTo || "..."})`
                          : ""}
                      </div>
                      <button
                        onClick={() => setFilter("All Time")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: 8,
                          background: "rgba(0,0,0,0.05)",
                          border: "1px solid rgba(0,0,0,0.07)",
                          color: "#1e2433",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: font,
                        }}
                      >
                        Show All Time
                      </button>
                    </div>
                  ) : (
                    filter !== "All Time" && (
                      <div
                        style={{
                          background: "rgba(0,201,167,0.07)",
                          border: "1px solid rgba(0,201,167,0.18)",
                          borderRadius: 10,
                          padding: "9px 16px",
                          marginBottom: 16,
                          fontSize: 11,
                          color: "#00c9a7",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          Filtered: <strong>{filter}</strong>
                        </span>
                        <span style={{ color: "#9ca3af" }}>·</span>
                        <span>
                          <strong style={{ color: "#1e2433" }}>
                            {filteredData.length}
                          </strong>{" "}
                          month{filteredData.length !== 1 ? "s" : ""}
                        </span>
                        {filteredData.length > 0 && (
                          <span style={{ color: "#9ca3af" }}>·</span>
                        )}
                        {filteredData.length > 0 && (
                          <span style={{ color: "#4b5563" }}>
                            {filteredData[0].label} →{" "}
                            {filteredData[filteredData.length - 1].label}
                          </span>
                        )}
                        {customFrom && (
                          <span>
                            · From: <strong>{customFrom}</strong>
                          </span>
                        )}
                        {customTo && (
                          <span>
                            · To: <strong>{customTo}</strong>
                          </span>
                        )}
                        <button
                          onClick={() => setFilter("All Time")}
                          style={{
                            marginLeft: "auto",
                            padding: "3px 10px",
                            borderRadius: 6,
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.06)",
                            color: "#4b5563",
                            fontSize: 10,
                            cursor: "pointer",
                            fontFamily: font,
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    )
                  )}
                  {/* KPI Summary Cards — 12 categories */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill,minmax(170px,1fr))",
                      gap: 12,
                      marginBottom: 20,
                    }}
                  >
                    {Object.entries(KPI_GROUPS)
                      .filter(([, g]) => !g.hidden)
                      .map(([name, g]) => {
                        const total = g.kpis.reduce(
                          (s, k) => s + (T[k] || 0),
                          0,
                        );
                        const periodLabel =
                          filteredData.length === 0
                            ? "No data"
                            : filter === "All Time"
                              ? "All time total"
                              : filteredData.length === 1
                                ? filteredData[0].label
                                : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`;
                        return (
                          <div
                            key={name}
                            className="kpi-card"
                            onClick={() => {
                              setFocusGroup(name);
                              setSelectedKPIs([]);
                            }}
                            style={{
                              background: "rgba(255,255,255,0.88)",
                              border: `1px solid rgba(255,255,255,0.07)`,
                              borderRadius: 14,
                              padding: "16px 18px",
                              cursor: "pointer",
                              backdropFilter: "blur(12px)",
                              transition: "all 0.2s",
                              borderTop: `3px solid ${g.color}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: g.color,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                                marginBottom: 8,
                              }}
                            >
                              {name}
                            </div>
                            <div
                              style={{
                                fontSize: filteredData.length === 0 ? 14 : 28,
                                fontWeight: 800,
                                color:
                                  filteredData.length === 0
                                    ? "#9ca3af"
                                    : "#1e2433",
                              }}
                            >
                              {filteredData.length === 0
                                ? "—"
                                : total.toLocaleString()}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#9ca3af",
                                marginTop: 4,
                              }}
                            >
                              {periodLabel}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#6b7280",
                                marginTop: 2,
                              }}
                            >
                              {g.kpis.length} indicators
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Row 1: Incident Trends (wide) + PTW */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr",
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    {/* Incident Trends - wide */}
                    <div className="card">
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e2433",
                          marginBottom: 4,
                        }}
                      >
                        Incident Trends
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#4b5563",
                          marginBottom: 14,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Fatality · LWC · First Aid · Near Miss</span>
                        <span
                          style={{
                            background:
                              chartData.length !== filteredData.length
                                ? "rgba(249,202,36,0.12)"
                                : "rgba(0,201,167,0.1)",
                            border: `1px solid ${chartData.length !== filteredData.length ? "rgba(249,202,36,0.3)" : "rgba(0,201,167,0.25)"}`,
                            color:
                              chartData.length !== filteredData.length
                                ? "#f9ca24"
                                : "#00c9a7",
                            borderRadius: 6,
                            padding: "2px 8px",
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                          }}
                        >
                          {chartData.length !== filteredData.length
                            ? "Monthly View"
                            : "Daily View"}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "flex-start",
                          gap: 4,
                          marginTop: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#e8f0fe"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["Fatality"] || 0) > 0 ? Math.min(((T["Fatality"] || 0) / (T_all["Fatality"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#ef4444",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Fatality"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#ef4444",
                              marginTop: 7,
                            }}
                          >
                            Fatality
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Fatality"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#e8f0fe"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#f97316"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["Lost Workday Case"] || 0) > 0 ? Math.min(((T["Lost Workday Case"] || 0) / (T_all["Lost Workday Case"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#f97316",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Lost Workday Case"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#f97316",
                              marginTop: 7,
                            }}
                          >
                            LWC
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Lost Workday Case"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#e8f0fe"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#eab308"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["First Aid Case"] || 0) > 0 ? Math.min(((T["First Aid Case"] || 0) / (T_all["First Aid Case"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#eab308",
                                  lineHeight: 1,
                                }}
                              >
                                {T["First Aid Case"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#eab308",
                              marginTop: 7,
                            }}
                          >
                            First Aid
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["First Aid Case"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#e8f0fe"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#8b5cf6"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["Near Miss"] || 0) > 0 ? Math.min(((T["Near Miss"] || 0) / (T_all["Near Miss"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#8b5cf6",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Near Miss"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#8b5cf6",
                              marginTop: 7,
                            }}
                          >
                            Near Miss
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Near Miss"] || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#94a3b8",
                          marginTop: 14,
                          paddingTop: 10,
                          borderTop: "1px solid #f1f5f9",
                          fontWeight: 500,
                        }}
                      >
                        {filteredData.length > 0
                          ? filteredData.length === 1
                            ? filteredData[0].label
                            : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                          : filter}
                      </div>
                    </div>
                    {/* PTW Distribution */}
                    <div className="card">
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e2433",
                          marginBottom: 4,
                        }}
                      >
                        PTW Distribution
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#4b5563",
                          marginBottom: 14,
                        }}
                      >
                        Permit to Work breakdown
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 16,
                        }}
                      >
                        <PieChart width={150} height={150}>
                          <Pie
                            data={[
                              {
                                name: "Cold Work",
                                value: T["Cold Work PTW Issued"] || 0,
                              },
                              {
                                name: "Hot Work",
                                value: T["Hot Work PTW Issued"] || 0,
                              },
                              { name: "Audits", value: T["PTW Audits"] || 0 },
                            ]}
                            cx={70}
                            cy={70}
                            innerRadius={40}
                            outerRadius={65}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {["#22c55e", "#ef4444", "#38bdf8"].map((c, i) => (
                              <Cell key={i} fill={c} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                        <div>
                          {[
                            ["Cold Work", "#22c55e", "Cold Work PTW Issued"],
                            ["Hot Work", "#ef4444", "Hot Work PTW Issued"],
                            ["Audits", "#38bdf8", "PTW Audits"],
                          ].map(([name, color, key]) => (
                            <div
                              key={name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                marginBottom: 6,
                              }}
                            >
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: color,
                                }}
                              />
                              <span style={{ fontSize: 11, color: "#4b5563" }}>
                                {name}:
                              </span>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#1e2433",
                                }}
                              >
                                {T[key] || 0}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: TBT & Leadership */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    <div className="card">
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e2433",
                          marginBottom: 4,
                        }}
                      >
                        TBT & HSE Sessions
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#4b5563",
                          marginBottom: 14,
                        }}
                      >
                        Induction · TBT · SSD · Briefing
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "flex-start",
                          gap: 4,
                          marginTop: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#2563eb"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["HSE Induction Conducted"] || 0) > 0 ? Math.min(((T["HSE Induction Conducted"] || 0) / (T_all["HSE Induction Conducted"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#2563eb",
                                  lineHeight: 1,
                                }}
                              >
                                {T["HSE Induction Conducted"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#2563eb",
                              marginTop: 7,
                            }}
                          >
                            Induction
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["HSE Induction Conducted"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#059669"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["TBT Session Conducted"] || 0) > 0 ? Math.min(((T["TBT Session Conducted"] || 0) / (T_all["TBT Session Conducted"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#059669",
                                  lineHeight: 1,
                                }}
                              >
                                {T["TBT Session Conducted"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#059669",
                              marginTop: 7,
                            }}
                          >
                            TBT
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["TBT Session Conducted"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#7c3aed"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["Safety Stand Down (SSD)"] || 0) > 0 ? Math.min(((T["Safety Stand Down (SSD)"] || 0) / (T_all["Safety Stand Down (SSD)"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#7c3aed",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Safety Stand Down (SSD)"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#7c3aed",
                              marginTop: 7,
                            }}
                          >
                            SSD
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Safety Stand Down (SSD)"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="100" height="100" viewBox="0 0 100 100">
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="11"
                              />
                              <circle
                                cx="50"
                                cy="50"
                                r="38"
                                fill="none"
                                stroke="#d97706"
                                strokeWidth="11"
                                strokeDasharray={`${(T_all["HSE Briefing Session"] || 0) > 0 ? Math.min(((T["HSE Briefing Session"] || 0) / (T_all["HSE Briefing Session"] || 1)) * 238.76, 238.76).toFixed(1) : 0} 238.76`}
                                strokeLinecap="round"
                                transform="rotate(-90 50 50)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: "#d97706",
                                  lineHeight: 1,
                                }}
                              >
                                {T["HSE Briefing Session"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                selected
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#d97706",
                              marginTop: 7,
                            }}
                          >
                            Briefing
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#64748b",
                              marginTop: 2,
                            }}
                          >
                            All time:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["HSE Briefing Session"] || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#94a3b8",
                          marginTop: 14,
                          paddingTop: 10,
                          borderTop: "1px solid #f1f5f9",
                          fontWeight: 500,
                        }}
                      >
                        {filteredData.length > 0
                          ? filteredData.length === 1
                            ? filteredData[0].label
                            : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                          : filter}
                      </div>
                    </div>
                    <div className="card">
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e2433",
                          marginBottom: 4,
                        }}
                      >
                        Leadership Audits
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#4b5563",
                          marginBottom: 14,
                        }}
                      >
                        Field Audits · Site Visit · Stop Card · TBT · Walkdown
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "flex-start",
                          gap: 2,
                          marginTop: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="96" height="96" viewBox="0 0 96 96">
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="10"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#0284c7"
                                strokeWidth="10"
                                strokeDasharray={`${(T_all["Leadership Field Audits / Walkdown"] || 0) > 0 ? Math.min(((T["Leadership Field Audits / Walkdown"] || 0) / (T_all["Leadership Field Audits / Walkdown"] || 1)) * 226.19, 226.19).toFixed(1) : 0} 226.19`}
                                strokeLinecap="round"
                                transform="rotate(-90 48 48)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#0284c7",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Leadership Field Audits / Walkdown"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                sel
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#0284c7",
                              marginTop: 6,
                              textAlign: "center",
                            }}
                          >
                            Field Audits
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#64748b",
                              marginTop: 1,
                            }}
                          >
                            All:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Leadership Field Audits / Walkdown"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="96" height="96" viewBox="0 0 96 96">
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="10"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#16a34a"
                                strokeWidth="10"
                                strokeDasharray={`${(T_all["Management Site Visit"] || 0) > 0 ? Math.min(((T["Management Site Visit"] || 0) / (T_all["Management Site Visit"] || 1)) * 226.19, 226.19).toFixed(1) : 0} 226.19`}
                                strokeLinecap="round"
                                transform="rotate(-90 48 48)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#16a34a",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Management Site Visit"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                sel
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#16a34a",
                              marginTop: 6,
                              textAlign: "center",
                            }}
                          >
                            Site Visit
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#64748b",
                              marginTop: 1,
                            }}
                          >
                            All:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Management Site Visit"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="96" height="96" viewBox="0 0 96 96">
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="10"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#dc2626"
                                strokeWidth="10"
                                strokeDasharray={`${(T_all["Management Stop Card"] || 0) > 0 ? Math.min(((T["Management Stop Card"] || 0) / (T_all["Management Stop Card"] || 1)) * 226.19, 226.19).toFixed(1) : 0} 226.19`}
                                strokeLinecap="round"
                                transform="rotate(-90 48 48)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#dc2626",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Management Stop Card"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                sel
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#dc2626",
                              marginTop: 6,
                              textAlign: "center",
                            }}
                          >
                            Stop Card
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#64748b",
                              marginTop: 1,
                            }}
                          >
                            All:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Management Stop Card"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="96" height="96" viewBox="0 0 96 96">
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="10"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#9333ea"
                                strokeWidth="10"
                                strokeDasharray={`${(T_all["Management TBT"] || 0) > 0 ? Math.min(((T["Management TBT"] || 0) / (T_all["Management TBT"] || 1)) * 226.19, 226.19).toFixed(1) : 0} 226.19`}
                                strokeLinecap="round"
                                transform="rotate(-90 48 48)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#9333ea",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Management TBT"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                sel
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#9333ea",
                              marginTop: 6,
                              textAlign: "center",
                            }}
                          >
                            Mgmt TBT
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#64748b",
                              marginTop: 1,
                            }}
                          >
                            All:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Management TBT"] || 0}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            width: 1,
                            background: "#f1f5f9",
                            alignSelf: "stretch",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg width="96" height="96" viewBox="0 0 96 96">
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#f1f5f9"
                                strokeWidth="10"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="36"
                                fill="none"
                                stroke="#ea580c"
                                strokeWidth="10"
                                strokeDasharray={`${(T_all["Management Walkdown"] || 0) > 0 ? Math.min(((T["Management Walkdown"] || 0) / (T_all["Management Walkdown"] || 1)) * 226.19, 226.19).toFixed(1) : 0} 226.19`}
                                strokeLinecap="round"
                                transform="rotate(-90 48 48)"
                              />
                            </svg>
                            <div
                              style={{
                                position: "absolute",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#ea580c",
                                  lineHeight: 1,
                                }}
                              >
                                {T["Management Walkdown"] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 600,
                                  color: "#94a3b8",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                  marginTop: 1,
                                }}
                              >
                                sel
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#ea580c",
                              marginTop: 6,
                              textAlign: "center",
                            }}
                          >
                            Walkdown
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#64748b",
                              marginTop: 1,
                            }}
                          >
                            All:{" "}
                            <span style={{ fontWeight: 700, color: "#374151" }}>
                              {T_all["Management Walkdown"] || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#94a3b8",
                          marginTop: 14,
                          paddingTop: 10,
                          borderTop: "1px solid #f1f5f9",
                          fontWeight: 500,
                        }}
                      >
                        {filteredData.length > 0
                          ? filteredData.length === 1
                            ? filteredData[0].label
                            : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                          : filter}
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Training Overview + Diversity Index */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 1fr",
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    {/* Training Overview */}
                    <div className="card">
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e2433",
                          marginBottom: 4,
                        }}
                      >
                        Training Overview
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#4b5563",
                          marginBottom: 14,
                        }}
                      >
                        Sessions · Participants · Hours
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 28,
                        }}
                      >
                        <PieChart width={150} height={150}>
                          <Pie
                            data={[
                              {
                                name: "Training",
                                value: T["Training Conducted"] || 0,
                              },
                              {
                                name: "Video",
                                value: T["Video Training Conducted"] || 0,
                              },
                              {
                                name: "Lesson Learned",
                                value:
                                  T["Lesson Learned Training Conducted"] || 0,
                              },
                            ]}
                            cx={70}
                            cy={70}
                            innerRadius={38}
                            outerRadius={62}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {["#4facfe", "#a29bfe", "#00c9a7"].map((c, i) => (
                              <Cell key={i} fill={c} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 14,
                          }}
                        >
                          {[
                            [
                              "Training",
                              "#4facfe",
                              "Training Conducted",
                              "Training No. of Participants",
                              "Training Hours Spent",
                            ],
                            [
                              "Video",
                              "#a29bfe",
                              "Video Training Conducted",
                              "Video No. of Participants",
                              "Video Hours Spent",
                            ],
                            [
                              "Lesson Learned",
                              "#00c9a7",
                              "Lesson Learned Training Conducted",
                              "LL No. of Participants",
                              "LL Hours Spent",
                            ],
                          ].map(([name, color, k1, k2, k3]) => (
                            <div key={name} style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: color,
                                  margin: "0 auto 5px",
                                }}
                              />
                              <div
                                style={{
                                  fontSize: 10,
                                  color: color,
                                  fontWeight: 700,
                                  marginBottom: 7,
                                }}
                              >
                                {name}
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: "#6b7280",
                                  marginBottom: 1,
                                }}
                              >
                                Sessions
                              </div>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  color: "#1e2433",
                                  marginBottom: 5,
                                }}
                              >
                                {T[k1] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: "#6b7280",
                                  marginBottom: 1,
                                }}
                              >
                                Participants
                              </div>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  color: "#1e2433",
                                  marginBottom: 5,
                                }}
                              >
                                {T[k2] || 0}
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: "#6b7280",
                                  marginBottom: 1,
                                }}
                              >
                                Hours
                              </div>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  color: "#1e2433",
                                }}
                              >
                                {T[k3] || 0}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#94a3b8",
                          marginTop: 14,
                          paddingTop: 10,
                          borderTop: "1px solid #f1f5f9",
                          fontWeight: 500,
                        }}
                      >
                        {filteredData.length > 0
                          ? filteredData.length === 1
                            ? filteredData[0].label
                            : `${filteredData[0].label} – ${filteredData[filteredData.length - 1].label}`
                          : filter}
                      </div>
                    </div>

                    {/* POB Card */}
                    <div
                      className="card"
                      style={{
                        background: "rgba(248,250,255,0.97)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Glow blobs */}
                      <div
                        style={{
                          position: "absolute",
                          top: -30,
                          left: -30,
                          width: 140,
                          height: 140,
                          borderRadius: "50%",
                          background: "rgba(79,172,254,0.12)",
                          filter: "blur(40px)",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: -30,
                          right: -30,
                          width: 140,
                          height: 140,
                          borderRadius: "50%",
                          background: "rgba(255,71,87,0.12)",
                          filter: "blur(40px)",
                          pointerEvents: "none",
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#4b5563",
                            letterSpacing: 2,
                            textTransform: "uppercase",
                          }}
                        >
                          POB
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {["Daily", "All Time"].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => setPobFilter(opt)}
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: 6,
                                border: "none",
                                cursor: "pointer",
                                background:
                                  pobFilter === opt ? "#0078d4" : "#f1f5f9",
                                color: pobFilter === opt ? "#fff" : "#64748b",
                                transition: "all 0.15s",
                              }}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 20,
                        }}
                      >
                        {/* EXPAT side */}
                        <div style={{ textAlign: "center" }}>
                          {/* Person icon */}
                          <svg
                            width="36"
                            height="44"
                            viewBox="0 0 36 44"
                            fill="none"
                          >
                            <circle
                              cx="18"
                              cy="10"
                              r="9"
                              fill="#4facfe"
                              opacity="0.9"
                            />
                            <path
                              d="M2 40 Q2 26 18 26 Q34 26 34 40"
                              fill="#4facfe"
                              opacity="0.9"
                            />
                          </svg>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#4facfe",
                              fontWeight: 600,
                              letterSpacing: 1,
                              textTransform: "uppercase",
                              marginTop: 4,
                            }}
                          >
                            Expat
                          </div>
                          <div
                            style={{
                              fontSize: 30,
                              fontWeight: 900,
                              color: "#4facfe",
                              lineHeight: 1,
                            }}
                          >
                            {pobData.expat || 0}
                          </div>
                        </div>

                        {/* Centre donut */}
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PieChart width={130} height={130}>
                            <Pie
                              data={[
                                { name: "Expat", value: pobData.expat || 0 },
                                { name: "Local", value: pobData.local || 0 },
                              ]}
                              cx={60}
                              cy={60}
                              innerRadius={42}
                              outerRadius={60}
                              startAngle={90}
                              endAngle={-270}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              <Cell fill="#4facfe" />
                              <Cell fill="#ff4757" />
                            </Pie>
                          </PieChart>
                          {/* Centre label */}
                          <div
                            style={{
                              position: "absolute",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 20,
                                fontWeight: 900,
                                color: "#1e2433",
                                lineHeight: 1,
                              }}
                            >
                              {pobData.pob || 0}
                            </div>
                          </div>
                        </div>

                        {/* LOCAL side */}
                        <div style={{ textAlign: "center" }}>
                          <svg
                            width="36"
                            height="44"
                            viewBox="0 0 36 44"
                            fill="none"
                          >
                            <circle
                              cx="18"
                              cy="10"
                              r="9"
                              fill="#ff4757"
                              opacity="0.9"
                            />
                            <path
                              d="M2 40 Q2 26 18 26 Q34 26 34 40"
                              fill="#ff4757"
                              opacity="0.9"
                            />
                          </svg>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#ff4757",
                              fontWeight: 600,
                              letterSpacing: 1,
                              textTransform: "uppercase",
                              marginTop: 4,
                            }}
                          >
                            Local
                          </div>
                          <div
                            style={{
                              fontSize: 30,
                              fontWeight: 900,
                              color: "#ff4757",
                              lineHeight: 1,
                            }}
                          >
                            {pobData.local || 0}
                          </div>
                        </div>
                      </div>

                      {/* Legend bar */}
                      <div
                        style={{
                          marginTop: 14,
                          display: "flex",
                          gap: 6,
                          height: 6,
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            flex: pobData.expat || 0,
                            background:
                              "linear-gradient(90deg,#4facfe,#74b9ff)",
                            borderRadius: "6px 0 0 6px",
                            minWidth: (pobData.expat || 0) > 0 ? 4 : 0,
                          }}
                        />
                        <div
                          style={{
                            flex: pobData.local || 0,
                            background:
                              "linear-gradient(90deg,#ff6b81,#ff4757)",
                            borderRadius: "0 6px 6px 0",
                            minWidth: (pobData.local || 0) > 0 ? 4 : 0,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: 5,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9,
                            color: "#4facfe",
                            fontWeight: 600,
                          }}
                        >
                          {pobData.pob > 0
                            ? Math.round(
                                ((pobData.expat || 0) * 100) / pobData.pob,
                              )
                            : 0}
                          % Expat
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            color: "#ff4757",
                            fontWeight: 600,
                          }}
                        >
                          {pobData.pob > 0
                            ? Math.round(
                                ((pobData.local || 0) * 100) / pobData.pob,
                              )
                            : 0}
                          % Local
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ── ALL DATA TABLE ── */}
                  <div className="card" style={{ marginTop: 18 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 14,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#1e2433",
                          }}
                        >
                          All Entries
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#9ca3af",
                            marginTop: 2,
                          }}
                        >
                          Row click karo to edit · {activeData.length} records
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          background: "rgba(255,255,255,0.88)",
                          border: "1px solid rgba(255,255,255,0.07)",
                          borderRadius: 6,
                          padding: "3px 10px",
                        }}
                      >
                        {activeData.length} rows
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          borderCollapse: "collapse",
                          fontSize: 11,
                          minWidth: "100%",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: "1px solid rgba(0,0,0,0.05)",
                              position: "sticky",
                              top: 0,
                              background: "rgba(248,250,255,0.97)",
                              zIndex: 1,
                            }}
                          >
                            <td
                              style={{
                                padding: "8px 14px",
                                color: "#9ca3af",
                                fontWeight: 700,
                                fontSize: 10,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                minWidth: 110,
                              }}
                            >
                              Date
                            </td>
                            {Object.entries(KPI_GROUPS)
                              .filter(([, g]) => !g.hidden)
                              .map(([gName, g]) =>
                                g.kpis.map((kpi) => (
                                  <td
                                    key={kpi}
                                    style={{
                                      padding: "8px 10px",
                                      color: g.color,
                                      fontWeight: 600,
                                      fontSize: 9,
                                      textTransform: "uppercase",
                                      whiteSpace: "nowrap",
                                      minWidth: 120,
                                      width: 120,
                                      letterSpacing: 0.3,
                                      textAlign: "center",
                                    }}
                                  >
                                    {kpi}
                                  </td>
                                )),
                              )}
                          </tr>
                        </thead>
                        <tbody>
                          {[...activeData]
                            .sort((a, b) => b.date.localeCompare(a.date))
                            .slice(
                              (tablePage - 1) * TABLE_PAGE_SIZE,
                              tablePage * TABLE_PAGE_SIZE,
                            )
                            .map((row, i) => (
                              <tr
                                key={row.date + i}
                                style={{
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.88)",
                                  transition: "background 0.12s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.background =
                                    "rgba(255,255,255,0.88)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.background =
                                    "transparent")
                                }
                              >
                                <td
                                  style={{
                                    padding: "6px 10px 6px 14px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    {/* Edit/Delete — admin only */}
                                    {currentUser?.role === "admin" && (
                                      <>
                                        {/* Edit button */}
                                        <button
                                          onClick={() => {
                                            setEditRow(row);
                                            setEditValues({ ...row });
                                            setEditStep(0);
                                            setConfirmDelete(null);
                                          }}
                                          style={{
                                            padding: "3px 10px",
                                            borderRadius: 6,
                                            background: "rgba(79,172,254,0.1)",
                                            border:
                                              "1px solid rgba(79,172,254,0.25)",
                                            color: "#4facfe",
                                            fontSize: 10,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            fontFamily: font,
                                            flexShrink: 0,
                                          }}
                                        >
                                          Edit
                                        </button>
                                        {/* Delete / Confirm inline */}
                                        {confirmDelete === row.date ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 4,
                                              alignItems: "center",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: 10,
                                                color: "#ff4757",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Sure?
                                            </span>
                                            <button
                                              onClick={async () => {
                                                try {
                                                  await apiFetch(
                                                    `/api/entries/${row.date}`,
                                                    { method: "DELETE" },
                                                  );
                                                  await loadEntriesFromAPI();
                                                } catch (err) {
                                                  alert(
                                                    "Delete failed: " +
                                                      (err.message ||
                                                        "Check backend."),
                                                  );
                                                }
                                                setConfirmDelete(null);
                                              }}
                                              style={{
                                                padding: "3px 8px",
                                                borderRadius: 6,
                                                background:
                                                  "rgba(255,71,87,0.85)",
                                                border: "none",
                                                color: "#1e2433",
                                                fontSize: 10,
                                                fontWeight: 700,
                                                cursor: "pointer",
                                                fontFamily: font,
                                              }}
                                            >
                                              Yes
                                            </button>
                                            <button
                                              onClick={() =>
                                                setConfirmDelete(null)
                                              }
                                              style={{
                                                padding: "3px 8px",
                                                borderRadius: 6,
                                                background: "rgba(0,0,0,0.05)",
                                                border:
                                                  "1px solid rgba(0,0,0,0.10)",
                                                color: "#4b5563",
                                                fontSize: 10,
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                fontFamily: font,
                                              }}
                                            >
                                              No
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() =>
                                              setConfirmDelete(row.date)
                                            }
                                            style={{
                                              padding: "3px 10px",
                                              borderRadius: 6,
                                              background: "rgba(255,71,87,0.1)",
                                              border:
                                                "1px solid rgba(255,71,87,0.25)",
                                              color: "#ff4757",
                                              fontSize: 10,
                                              fontWeight: 600,
                                              cursor: "pointer",
                                              fontFamily: font,
                                              flexShrink: 0,
                                            }}
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </>
                                    )}
                                    <span
                                      style={{
                                        color: "#4b5563",
                                        fontWeight: 600,
                                        fontSize: 11,
                                        whiteSpace: "nowrap",
                                        marginLeft: 4,
                                      }}
                                    >
                                      {row.label || row.date}
                                    </span>
                                  </div>
                                </td>
                                {Object.entries(KPI_GROUPS)
                                  .filter(([, g]) => !g.hidden)
                                  .map(([gName, g]) =>
                                    g.kpis.map((kpi) => (
                                      <td
                                        key={kpi}
                                        onClick={() => {
                                          setEditRow(row);
                                          setEditValues({ ...row });
                                          setEditStep(0);
                                        }}
                                        style={{
                                          padding: "9px 10px",
                                          textAlign: "center",
                                          width: 120,
                                          minWidth: 120,
                                          color:
                                            (row[kpi] || 0) > 0
                                              ? g.color
                                              : "#2d3748",
                                          fontWeight:
                                            (row[kpi] || 0) > 0 ? 700 : 400,
                                          fontSize: 12,
                                          cursor: "pointer",
                                        }}
                                      >
                                        {row[kpi] ?? 0}
                                      </td>
                                    )),
                                  )}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {activeData.length > TABLE_PAGE_SIZE && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 4px 4px",
                          borderTop: "1px solid rgba(0,0,0,0.05)",
                          marginTop: 8,
                        }}
                      >
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          Showing {(tablePage - 1) * TABLE_PAGE_SIZE + 1}–
                          {Math.min(
                            tablePage * TABLE_PAGE_SIZE,
                            activeData.length,
                          )}{" "}
                          of {activeData.length} records
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            alignItems: "center",
                          }}
                        >
                          <button
                            onClick={() =>
                              setTablePage((p) => Math.max(1, p - 1))
                            }
                            disabled={tablePage === 1}
                            style={{
                              padding: "4px 12px",
                              borderRadius: 7,
                              fontSize: 12,
                              fontWeight: 600,
                              background:
                                tablePage === 1 ? "#f1f5f9" : "#0078d4",
                              color: tablePage === 1 ? "#9ca3af" : "#fff",
                              border: "none",
                              cursor: tablePage === 1 ? "default" : "pointer",
                            }}
                          >
                            ← Prev
                          </button>
                          {Array.from(
                            {
                              length: Math.ceil(
                                activeData.length / TABLE_PAGE_SIZE,
                              ),
                            },
                            (_, i) => i + 1,
                          ).map((pg) => (
                            <button
                              key={pg}
                              onClick={() => setTablePage(pg)}
                              style={{
                                padding: "4px 10px",
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 600,
                                background:
                                  tablePage === pg ? "#0078d4" : "#f1f5f9",
                                color: tablePage === pg ? "#fff" : "#374151",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              {pg}
                            </button>
                          ))}
                          <button
                            onClick={() =>
                              setTablePage((p) =>
                                Math.min(
                                  Math.ceil(
                                    activeData.length / TABLE_PAGE_SIZE,
                                  ),
                                  p + 1,
                                ),
                              )
                            }
                            disabled={
                              tablePage ===
                              Math.ceil(activeData.length / TABLE_PAGE_SIZE)
                            }
                            style={{
                              padding: "4px 12px",
                              borderRadius: 7,
                              fontSize: 12,
                              fontWeight: 600,
                              background:
                                tablePage ===
                                Math.ceil(activeData.length / TABLE_PAGE_SIZE)
                                  ? "#f1f5f9"
                                  : "#0078d4",
                              color:
                                tablePage ===
                                Math.ceil(activeData.length / TABLE_PAGE_SIZE)
                                  ? "#9ca3af"
                                  : "#fff",
                              border: "none",
                              cursor:
                                tablePage ===
                                Math.ceil(activeData.length / TABLE_PAGE_SIZE)
                                  ? "default"
                                  : "pointer",
                            }}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}{" "}
              {/* end activeData.length > 0 */}
            </div>
          )}
        </div>
      </div>

      {/* ── EDIT ROW MODAL ── */}
      {editRow &&
        (() => {
          const allGroups = Object.entries(KPI_GROUPS);
          const totalSteps = allGroups.length;
          const [groupName, groupData] = allGroups[editStep];

          async function handleEditSave() {
            try {
              await apiFetch(`/api/entries/${editRow.date}`, {
                method: "PUT",
                body: JSON.stringify(editValues),
              });
              await loadEntriesFromAPI();
            } catch (err) {
              alert(
                "Update failed: " +
                  (err.message || "Check backend connection."),
              );
              return;
            }
            setEditRow(null);
          }

          async function handleDelete() {
            try {
              await apiFetch(`/api/entries/${editRow.date}`, {
                method: "DELETE",
              });
              await loadEntriesFromAPI();
            } catch (err) {
              alert(
                "Delete failed: " +
                  (err.message || "Check backend connection."),
              );
              return;
            }
            setEditRow(null);
          }

          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.78)",
                backdropFilter: "blur(10px)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setEditRow(null)}
            >
              <div
                style={{
                  background: "rgba(252,253,255,0.99)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 22,
                  width: 560,
                  maxHeight: "88vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "20px 28px 14px",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#1e2433",
                      }}
                    >
                      Edit Entry
                    </div>
                    <div
                      style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}
                    >
                      {editRow.label || editRow.date} · Step {editStep + 1}/
                      {totalSteps} — {groupName}
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <button
                      onClick={handleDelete}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        background: "rgba(255,71,87,0.1)",
                        border: "1px solid rgba(255,71,87,0.25)",
                        color: "#ff4757",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setEditRow(null)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#6b7280",
                        fontSize: 22,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div
                  style={{
                    height: 3,
                    background: "rgba(0,0,0,0.04)",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: `linear-gradient(90deg,${groupData.color},${groupData.color}88)`,
                      borderRadius: 3,
                      width: `${((editStep + 1) / totalSteps) * 100}%`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>

                {/* Fields */}
                <div
                  style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: groupData.color,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        color: groupData.color,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      {groupData.label}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    {groupData.kpis.map((kpi) => (
                      <div key={kpi}>
                        <label
                          style={{
                            fontSize: 10,
                            color: "#6b7280",
                            fontWeight: 600,
                            display: "block",
                            marginBottom: 5,
                            lineHeight: 1.4,
                          }}
                        >
                          {kpi}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={editValues[kpi] ?? 0}
                          onChange={(e) =>
                            setEditValues((prev) => ({
                              ...prev,
                              [kpi]: Number(e.target.value) || 0,
                            }))
                          }
                          style={{
                            width: "100%",
                            background: "rgba(0,0,0,0.03)",
                            border: `1px solid ${(editValues[kpi] || 0) > 0 ? groupData.color + "60" : "rgba(0,0,0,0.06)"}`,
                            borderRadius: 8,
                            padding: "9px 12px",
                            color:
                              (editValues[kpi] || 0) > 0
                                ? "#1e2433"
                                : "#6b7280",
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: font,
                            outline: "none",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div
                  style={{
                    padding: "14px 28px",
                    borderTop: "1px solid rgba(0,0,0,0.09)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <button
                    onClick={() => setEditStep((s) => Math.max(0, s - 1))}
                    disabled={editStep === 0}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 10,
                      background: "rgba(0,0,0,0.03)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      color: editStep === 0 ? "#2d3748" : "#4b5563",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: editStep === 0 ? "default" : "pointer",
                      fontFamily: font,
                    }}
                  >
                    Back
                  </button>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>
                    {editStep + 1} / {totalSteps}
                  </span>
                  {editStep < totalSteps - 1 ? (
                    <button
                      onClick={() => setEditStep((s) => s + 1)}
                      style={{
                        padding: "8px 22px",
                        borderRadius: 10,
                        background: `rgba(${groupData.color},0.15)`,
                        background: "rgba(0,0,0,0.05)",
                        border: "1px solid rgba(0,0,0,0.07)",
                        color: "#1e2433",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handleEditSave}
                      style={{
                        padding: "8px 22px",
                        borderRadius: 10,
                        background: "linear-gradient(135deg,#00c9a7,#00b894)",
                        border: "none",
                        color: "#f0f4ff",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Save Changes
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── EXPORT MODAL ── */}
      {showExport && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowExport(false)}
        >
          <div
            style={{
              background: "rgba(252,253,255,0.99)",
              border: "1px solid rgba(0,0,0,0.06)",
              borderRadius: 20,
              padding: 32,
              width: 420,
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 17, fontWeight: 700, color: "#1e2433" }}
                >
                  Export Data
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  Currently showing:{" "}
                  <strong style={{ color: "#1e2433" }}>{filter}</strong> ·{" "}
                  <strong style={{ color: "#00c9a7" }}>
                    {filteredData.length} months
                  </strong>
                </div>
              </div>
              <button
                onClick={() => setShowExport(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  fontSize: 20,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            {/* What's included */}
            <div
              style={{
                background: "rgba(255,255,255,0.75)",
                border: "1px solid rgba(0,0,0,0.09)",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#4b5563",
                  marginBottom: 10,
                }}
              >
                Export includes
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                }}
              >
                {Object.entries(KPI_GROUPS)
                  .filter(([, g]) => !g.hidden)
                  .map(([name, g]) => (
                    <div
                      key={name}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: g.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 10, color: "#6b7280" }}>
                        {name}
                      </span>
                    </div>
                  ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 10, color: "#9ca3af" }}>
                All {Object.values(KPI_GROUPS).flatMap((g) => g.kpis).length}{" "}
                KPIs · {filteredData.length} month
                {filteredData.length !== 1 ? "s" : ""} · includes TOTAL row
              </div>
            </div>

            {/* Export buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => {
                  exportCSV();
                  setShowExport(false);
                }}
                style={{
                  width: "100%",
                  padding: "14px 20px",
                  borderRadius: 12,
                  background: "rgba(0,201,167,0.12)",
                  border: "1px solid rgba(0,201,167,0.3)",
                  color: "#00c9a7",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: font,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                Export as CSV
                <span
                  style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}
                >
                  · opens in Excel/Sheets
                </span>
              </button>
              <button
                onClick={() => {
                  exportExcel();
                  setShowExport(false);
                }}
                style={{
                  width: "100%",
                  padding: "14px 20px",
                  borderRadius: 12,
                  background: "rgba(79,172,254,0.12)",
                  border: "1px solid rgba(79,172,254,0.3)",
                  color: "#4facfe",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: font,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                Export as Excel (.xlsx)
                <span
                  style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}
                >
                  · formatted workbook
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── UPLOAD MODAL ── */}
      {showUpload && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowUpload(false)}
        >
          <div
            style={{
              background: "rgba(252,253,255,0.99)",
              border: "1px solid rgba(0,0,0,0.06)",
              borderRadius: 20,
              padding: 32,
              width: 480,
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 17, fontWeight: 700, color: "#1e2433" }}
                >
                  Upload Excel Data
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  Replace dashboard data with your Excel file
                </div>
              </div>
              <button
                onClick={() => setShowUpload(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  fontSize: 20,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                Close
              </button>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border:
                  "2px dashed " +
                  (isDragging ? "#4facfe" : "rgba(255,255,255,0.12)"),
                borderRadius: 14,
                padding: "36px 20px",
                textAlign: "center",
                cursor: "pointer",
                background: isDragging
                  ? "rgba(79,172,254,0.06)"
                  : "rgba(255,255,255,0.65)",
                transition: "all 0.2s",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 6,
                }}
              >
                Drop Excel file here
              </div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                or click to browse · .xlsx .xls supported
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files[0]) parseExcel(e.target.files[0]);
                }}
              />
            </div>

            {uploadError && (
              <div
                style={{
                  background: "rgba(255,71,87,0.1)",
                  border: "1px solid rgba(255,71,87,0.3)",
                  borderRadius: 8,
                  padding: "10px 14px",
                  color: "#ff6b81",
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {uploadError}
              </div>
            )}

            <div
              style={{
                background: "rgba(255,255,255,0.75)",
                border: "1px solid rgba(0,0,0,0.09)",
                borderRadius: 10,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#4b5563",
                  marginBottom: 8,
                }}
              >
                Excel Format Guide
              </div>
              <div style={{ fontSize: 10, color: "#6b7280", lineHeight: 1.8 }}>
                • First row = column headers (KPI names)
                <br />• First column ={" "}
                <strong style={{ color: "#4b5563" }}>Date</strong> or{" "}
                <strong style={{ color: "#4b5563" }}>Month</strong>
                <br />
                • Each row = one month of data
                <br />• Column names must match KPI names exactly
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD DATA MODAL ── */}
      {showAddData &&
        (() => {
          const allGroups = Object.entries(KPI_GROUPS);
          const totalSteps = allGroups.length + 1; // step 0 = date, steps 1..N = each group
          const currentGroup = addStep > 0 ? allGroups[addStep - 1] : null;
          const [groupName, groupData] = currentGroup || [];

          async function handleSave() {
            const dateStr = addDate;
            const parts = dateStr.split("-");
            const d = new Date(
              parseInt(parts[0]),
              parseInt(parts[1]) - 1,
              parseInt(parts[2]),
            );
            const label =
              addLabel.trim() ||
              d.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              });
            const newEntry = { date: dateStr, label, ...addValues };
            Object.values(KPI_GROUPS)
              .flatMap((g) => g.kpis)
              .forEach((k) => {
                if (newEntry[k] === undefined) newEntry[k] = 0;
              });
            const exists = uploadData.some((r) => r.date === dateStr);
            try {
              if (exists) {
                await apiFetch(`/api/entries/${dateStr}`, {
                  method: "PUT",
                  body: JSON.stringify(newEntry),
                });
              } else {
                await apiFetch("/api/entries", {
                  method: "POST",
                  body: JSON.stringify(newEntry),
                });
              }
              await loadEntriesFromAPI();
              setAddSaved(true);
              setTimeout(() => {
                setShowAddData(false);
                setFilter("All Time");
              }, 1400);
            } catch (err) {
              alert(
                "Save failed: " +
                  (err.message ||
                    "Backend se connection nahi hai. Server check karo."),
              );
            }
          }

          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.75)",
                backdropFilter: "blur(10px)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setShowAddData(false)}
            >
              <div
                style={{
                  background: "rgba(252,253,255,0.99)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 22,
                  width: 560,
                  maxHeight: "88vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div
                  style={{
                    padding: "22px 28px 16px",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: "#1e2433",
                      }}
                    >
                      + Add Daily Data
                    </div>
                    <div
                      style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}
                    >
                      {addStep === 0
                        ? "Step 1 — Select date"
                        : `Step ${addStep + 1} of ${totalSteps} — ${groupName}`}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddData(false)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#6b7280",
                      fontSize: 22,
                      cursor: "pointer",
                      lineHeight: 1,
                    }}
                  >
                    Close
                  </button>
                </div>

                {/* Progress Bar */}
                <div
                  style={{
                    height: 3,
                    background: "rgba(0,0,0,0.04)",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: "linear-gradient(90deg,#f9ca24,#fdcb6e)",
                      borderRadius: 3,
                      width: `${(addStep / (totalSteps - 1)) * 100}%`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>

                {/* Step Content */}
                <div
                  style={{ flex: 1, overflowY: "auto", padding: "22px 28px" }}
                >
                  {addSaved ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                      <div
                        style={{
                          fontSize: 17,
                          fontWeight: 700,
                          color: "#00c9a7",
                        }}
                      >
                        Data Saved!
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}
                      >
                        Dashboard update ho gaya
                      </div>
                    </div>
                  ) : addStep === 0 ? (
                    /* ── Step 0: Date Selection ── */
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#4b5563",
                          marginBottom: 20,
                          lineHeight: 1.7,
                        }}
                      >
                        Kisi bhi din ka HSE data yahan enter karein. Agar us
                        date ka data pehle se exist karta hai to wo replace ho
                        jayega.
                      </div>
                      <div style={{ marginBottom: 18 }}>
                        <label
                          style={{
                            fontSize: 11,
                            color: "#6b7280",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          Date
                        </label>
                        <input
                          type="date"
                          value={addDate}
                          onChange={(e) => setAddDate(e.target.value)}
                          style={{
                            width: "100%",
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            borderRadius: 10,
                            padding: "12px 14px",
                            color: "#1e2433",
                            fontSize: 14,
                            fontFamily: font,
                            outline: "none",
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label
                          style={{
                            fontSize: 11,
                            color: "#6b7280",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          Custom Label{" "}
                          <span
                            style={{
                              color: "#9ca3af",
                              fontWeight: 400,
                              textTransform: "none",
                            }}
                          >
                            (optional)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={addLabel}
                          onChange={(e) => setAddLabel(e.target.value)}
                          placeholder={`e.g. "Week 10" ya chhod do auto-fill ke liye`}
                          style={{
                            width: "100%",
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            borderRadius: 10,
                            padding: "12px 14px",
                            color: "#1e2433",
                            fontSize: 13,
                            fontFamily: font,
                            outline: "none",
                          }}
                        />
                      </div>
                      {/* Quick date buttons */}
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 16,
                          flexWrap: "wrap",
                        }}
                      >
                        {["Today", "Yesterday"].map((label) => {
                          const d = new Date();
                          if (label === "Yesterday") d.setDate(d.getDate() - 1);
                          const val = localDate(d);
                          return (
                            <button
                              key={label}
                              onClick={() => setAddDate(val)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: 8,
                                background:
                                  addDate === val
                                    ? "rgba(249,202,36,0.2)"
                                    : "rgba(0,0,0,0.03)",
                                border: `1px solid ${addDate === val ? "rgba(249,202,36,0.4)" : "rgba(0,0,0,0.06)"}`,
                                color: addDate === val ? "#f9ca24" : "#4b5563",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                                fontFamily: font,
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* ── Steps 1-N: KPI Group Fields ── */
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginBottom: 18,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: groupData.color,
                            flexShrink: 0,
                          }}
                        />
                        <div
                          style={{
                            fontSize: 12,
                            color: groupData.color,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          {groupData.label}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 12,
                        }}
                      >
                        {groupData.kpis.map((kpi) => (
                          <div key={kpi}>
                            <label
                              style={{
                                fontSize: 10,
                                color: "#6b7280",
                                fontWeight: 600,
                                display: "block",
                                marginBottom: 5,
                                lineHeight: 1.4,
                              }}
                            >
                              {kpi}
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={addValues[kpi] ?? ""}
                              onChange={(e) =>
                                setAddValues((prev) => ({
                                  ...prev,
                                  [kpi]: Number(e.target.value) || 0,
                                }))
                              }
                              placeholder="0"
                              style={{
                                width: "100%",
                                background: "rgba(0,0,0,0.03)",
                                border: `1px solid ${addValues[kpi] > 0 ? groupData.color + "60" : "rgba(0,0,0,0.06)"}`,
                                borderRadius: 8,
                                padding: "9px 12px",
                                color:
                                  addValues[kpi] > 0 ? "#1e2433" : "#6b7280",
                                fontSize: 14,
                                fontWeight: 700,
                                fontFamily: font,
                                outline: "none",
                                transition: "border 0.15s",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Buttons */}
                {!addSaved && (
                  <div
                    style={{
                      padding: "16px 28px",
                      borderTop: "1px solid rgba(0,0,0,0.09)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={() => setAddStep((s) => Math.max(0, s - 1))}
                      disabled={addStep === 0}
                      style={{
                        padding: "9px 20px",
                        borderRadius: 10,
                        background: "rgba(0,0,0,0.03)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        color: addStep === 0 ? "#2d3748" : "#4b5563",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: addStep === 0 ? "default" : "pointer",
                        fontFamily: font,
                      }}
                    >
                      Back
                    </button>
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>
                      {addStep}/{totalSteps - 1} groups filled
                    </div>
                    {addStep < totalSteps - 1 ? (
                      <button
                        onClick={() => setAddStep((s) => s + 1)}
                        disabled={addStep === 0 && !addDate}
                        style={{
                          padding: "9px 24px",
                          borderRadius: 10,
                          background: "rgba(249,202,36,0.18)",
                          border: "1px solid rgba(249,202,36,0.4)",
                          color: "#f9ca24",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: font,
                        }}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleSave}
                        style={{
                          padding: "9px 24px",
                          borderRadius: 10,
                          background: "linear-gradient(135deg,#f9ca24,#fdcb6e)",
                          border: "none",
                          color: "#f0f4ff",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: font,
                        }}
                      >
                        Save Entry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      {/* ── PROFILE DROPDOWN (root level for z-index) ── */}
      {showProfile && (
        <div
          onClick={() => setShowProfile(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999998,
            background: "transparent",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: 56,
              right: 16,
              width: 310,
              background: "rgba(252,253,255,0.99)",
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 16,
              boxShadow:
                "0 24px 80px rgba(0,0,0,0.9), 0 0 0 1px rgba(0,168,255,0.1)",
              overflow: "hidden",
              zIndex: 999999,
            }}
          >
            {/* Profile Header */}
            <div
              style={{
                padding: "20px 20px 14px",
                background:
                  "linear-gradient(135deg,rgba(0,120,212,0.06),rgba(0,168,255,0.03))",
                borderBottom: "1px solid rgba(0,0,0,0.09)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#00a8ff,#0078d4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ fontSize: 20, fontWeight: 700, color: "#1e2433" }}
                  >
                    {(currentUser?.username || "U").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 700, color: "#1e2433" }}
                  >
                    {currentUser?.username}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color:
                        currentUser?.role === "admin" ? "#f9ca24" : "#4b5563",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      marginTop: 2,
                    }}
                  >
                    {currentUser?.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid rgba(0,0,0,0.09)",
              }}
            >
              {[
                { id: "info", label: "Account" },
                { id: "password", label: "Change Password" },
                ...(currentUser?.role === "admin"
                  ? [{ id: "staff", label: "Staff Management" }]
                  : []),
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setProfileTab(t.id);
                    setProfileMsg({ text: "", ok: true });
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 0",
                    background: "none",
                    border: "none",
                    borderBottom:
                      profileTab === t.id
                        ? "2px solid #00a8ff"
                        : "2px solid transparent",
                    color: profileTab === t.id ? "#00a8ff" : "#6b7280",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ padding: "16px 20px 20px" }}>
              {profileTab === "info" && (
                <div>
                  <div style={{ marginBottom: 14 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "rgba(0,0,0,0.40)",
                        marginBottom: 6,
                        letterSpacing: 0.5,
                      }}
                    >
                      USERNAME
                    </label>
                    <input
                      value={newUsername || currentUser?.username}
                      onChange={(e) => setNewUsername(e.target.value)}
                      style={{
                        width: "100%",
                        background: "rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        borderRadius: 8,
                        padding: "9px 12px",
                        color: "#1e2433",
                        fontSize: 13,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "rgba(0,0,0,0.40)",
                        marginBottom: 6,
                        letterSpacing: 0.5,
                      }}
                    >
                      ROLE
                    </label>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.88)",
                        border: "1px solid rgba(0,0,0,0.05)",
                        borderRadius: 8,
                        padding: "9px 12px",
                        fontSize: 13,
                        color:
                          currentUser?.role === "admin" ? "#f9ca24" : "#4b5563",
                        fontWeight: 600,
                      }}
                    >
                      {(currentUser?.role || "viewer").toUpperCase()}
                    </div>
                  </div>
                  {profileMsg.text && profileTab === "info" && (
                    <div
                      style={{
                        padding: "8px 12px",
                        borderRadius: 7,
                        background: profileMsg.ok
                          ? "rgba(0,201,167,0.12)"
                          : "rgba(255,71,87,0.12)",
                        border: `1px solid ${profileMsg.ok ? "rgba(0,201,167,0.3)" : "rgba(255,71,87,0.3)"}`,
                        color: profileMsg.ok ? "#00c9a7" : "#ff4757",
                        fontSize: 12,
                        marginBottom: 12,
                      }}
                    >
                      {profileMsg.text}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (!newUsername || newUsername === currentUser?.username)
                        return setProfileMsg({
                          text: "Enter a new username.",
                          ok: false,
                        });
                      try {
                        await apiFetch("/api/auth/change-username", {
                          method: "PUT",
                          body: JSON.stringify({ newUsername }),
                        });
                        setCurrentUser((u) => ({
                          ...u,
                          username: newUsername,
                        }));
                        setProfileMsg({
                          text: "Username updated successfully!",
                          ok: true,
                        });
                      } catch (err) {
                        setProfileMsg({
                          text: err.message || "Failed to update username.",
                          ok: false,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "9px",
                      borderRadius: 8,
                      background: "linear-gradient(135deg,#00a8ff,#0078d4)",
                      border: "none",
                      color: "#1e2433",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      marginBottom: 8,
                    }}
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      padding: "9px",
                      borderRadius: 8,
                      background: "rgba(255,71,87,0.1)",
                      border: "1px solid rgba(255,71,87,0.25)",
                      color: "#ff4757",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}

              {profileTab === "password" && (
                <div>
                  {[
                    {
                      label: "CURRENT PASSWORD",
                      val: oldPass,
                      set: setOldPass,
                      ph: "Enter current password",
                    },
                    {
                      label: "NEW PASSWORD",
                      val: newPass,
                      set: setNewPass,
                      ph: "Min 6 characters",
                    },
                    {
                      label: "CONFIRM PASSWORD",
                      val: confirmPass,
                      set: setConfirmPass,
                      ph: "Repeat new password",
                    },
                  ].map((f) => (
                    <div key={f.label} style={{ marginBottom: 12 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: "rgba(0,0,0,0.40)",
                          marginBottom: 5,
                          letterSpacing: 0.5,
                        }}
                      >
                        {f.label}
                      </label>
                      <input
                        type="password"
                        value={f.val}
                        onChange={(e) => f.set(e.target.value)}
                        placeholder={f.ph}
                        style={{
                          width: "100%",
                          background: "rgba(0,0,0,0.04)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          borderRadius: 8,
                          padding: "9px 12px",
                          color: "#1e2433",
                          fontSize: 13,
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  ))}
                  {profileMsg.text && profileTab === "password" && (
                    <div
                      style={{
                        padding: "8px 12px",
                        borderRadius: 7,
                        background: profileMsg.ok
                          ? "rgba(0,201,167,0.12)"
                          : "rgba(255,71,87,0.12)",
                        border: `1px solid ${profileMsg.ok ? "rgba(0,201,167,0.3)" : "rgba(255,71,87,0.3)"}`,
                        color: profileMsg.ok ? "#00c9a7" : "#ff4757",
                        fontSize: 12,
                        marginBottom: 12,
                      }}
                    >
                      {profileMsg.text}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (!oldPass || !newPass || !confirmPass)
                        return setProfileMsg({
                          text: "All fields required.",
                          ok: false,
                        });
                      if (newPass.length < 6)
                        return setProfileMsg({
                          text: "Min 6 characters required.",
                          ok: false,
                        });
                      if (newPass !== confirmPass)
                        return setProfileMsg({
                          text: "Passwords do not match.",
                          ok: false,
                        });
                      try {
                        await apiFetch("/api/auth/change-password", {
                          method: "PUT",
                          body: JSON.stringify({
                            currentPassword: oldPass,
                            newPassword: newPass,
                          }),
                        });
                        setProfileMsg({
                          text: "Password changed successfully!",
                          ok: true,
                        });
                        setOldPass("");
                        setNewPass("");
                        setConfirmPass("");
                      } catch (err) {
                        setProfileMsg({
                          text: err.message || "Failed.",
                          ok: false,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "9px",
                      borderRadius: 8,
                      background: "linear-gradient(135deg,#00a8ff,#0078d4)",
                      border: "none",
                      color: "#1e2433",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Update Password
                  </button>
                </div>
              )}

              {profileTab === "staff" && currentUser?.role === "admin" && (
                <StaffManagement apiFetch={apiFetch} />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(0,0,0,0.06); border-radius:4px; }
        .card { background:rgba(255,255,255,0.88); border:1px solid rgba(255,255,255,0.07); border-radius:16px; padding:20px; backdrop-filter:blur(12px); box-shadow:0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(0,0,0,0.04); }
        .kpi-card:hover { background:rgba(0,0,0,0.05) !important; transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,0.35); }
        .sidebar-item { padding:6px 10px; cursor:pointer; border-radius:0 8px 8px 0; margin-bottom:2px; transition:all 0.15s; }
        .sidebar-item:hover { background:rgba(0,0,0,0.03); }
        .sidebar-item.active { background:rgba(0,0,0,0.04); }
        .fade { animation:fadeIn 0.25s ease; }
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
